import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CustomToolTip from "../components/CustomToolTip";
import TranslationTextFields from "../components/TranslationTextFields";
import { useApi } from '../hooks/useApi';
import { useLanguage } from "../hooks/useLanguage";

export default function EditColor({ userInfo, version: productVersion, pid: productPropId, id: modelPropId, onClose, fetchThroughProduct, viewStatic = false }) {
  // retrieve the id and pid either through params or props
  const { id: modelId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const version = productVersion ?? searchParams.get("version");
  const versionSearchParam = `${version != null ? `version=${version}` : ''}`
  const id = modelPropId || modelId;
  const pid = productPropId;

  // if user is not paying, publicFlag must be true
  const [data, setData] = useState({ publicFlag: userInfo?.isPaying ? false : true, hex: '#000000', localization: {} });
  const [isLoading, setIsLoading] = useState(false);
  // ref that references all translations
  const translationsRef = useRef();
  // if translations should be shown or not
  const [showTranslations, setShowTranslations] = useState(false);
  const { locale } = useLanguage();

  const isStatic = version != null || viewStatic || (id !== 'new' && !userInfo?.isAdmin && data.ownerId !== userInfo?.ownerId);

  const intl = useIntl();
  const api = useApi();

  const location = useLocation();
  const { fromTab } = location.state || {};

  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    const obj = { ...data, localization: getLanguageFields(data) };
    // field 'name' is not needed for server-communication (done through translations)
    delete obj.name;

    setIsLoading(true);
    if (id === "new") {
      api
        .post(`/color`, obj)
        .then((response) => {
          if (onClose) {
            onClose(response.data);
          } else {
            navigate(`/colors${fromTab != null ? `?tab=${fromTab}` : ''}`);
          }
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      api
        .put(`/color/${id}`, obj)
        .then((response) => {
          if (onClose) {
            onClose(response.data);
          } else {
            navigate(`/colors${fromTab != null ? `?tab=${fromTab}` : ''}`);
          }
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (id !== "new") {
      let fetchURL = '';
      if (fetchThroughProduct) {
        fetchURL = `/product/${pid}`;
      }
      fetchURL += `/color/${id}?${versionSearchParam}&localization=true&lang=${locale}`
      setIsLoading(true);
      api
        .get(fetchURL)
        .then(response => {
          setData(response.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, versionSearchParam]);

  const changeData = (event) => {
    setData(oldData => ({ ...oldData, [event.target.name]: event.target.value }));
  };

  const handlePublicChange = (event) => {
    setData(oldData => ({ ...oldData, publicFlag: event.target.checked }));
  };

  /**
     * Returns an object with key-value-pairs of translations.
     * If showTranslations = true, the specific translations are used.
     * If showTranslations = false, only uses the current locale.
     * @param {*} data 
     * @returns 
     */
  const getLanguageFields = (data) => {
    if (showTranslations) {
      // translationsRef is used
      if (translationsRef.current) {
        return translationsRef.current.getTranslations();
      } else {
        return {};
      }
    } else {
      // translationsRef is not used
      return { ...data.localization }
    }
  };

  /**
   * Sets the language-field of the current locale.
   * data.name and data.name<Language> is updated.
   * @param {*} value 
   */
  const setLocaleField = (value) => {
    setData(old => {
      const o = { ...old };
      o.localization[locale] = value;
      o.name = value;
      return o;
    });
  };

  const textFieldStyle = {
    width: '90%'
  }

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
        <FormattedMessage id="EditColor.heading" />
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container spacing={3} alignItems="center" sx={{ border: '1px dashed black', borderRadius: 3, margin: 0, width: "100%", paddingBottom: 2 }}>
          {
            showTranslations &&
            <TranslationTextFields ref={translationsRef} defaultTranslations={{ ...data.localization }}
              isStatic={isStatic} />
          }
          {
            !showTranslations &&
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                name={"name"}
                label={intl.formatMessage({ id: "EditColor.name" })}
                type="text"
                sx={textFieldStyle}
                value={data.name || ''}
                onChange={(event) => setLocaleField(event.target.value)}
                disabled={isStatic}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          }
          {
            <Grid item xs={12} container alignItems={'center'}>
              <CustomToolTip
                tooltip={showTranslations ? <FormattedMessage id='EditObject.hideTranslations' /> : <FormattedMessage id='EditObject.showTranslations' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowTranslations(old => {
                    if (old) {
                      // update localization if switching from multi-translation-text-fields to single field
                      if (translationsRef.current) {
                        const localization = translationsRef.current.getTranslations();
                        setData(data => ({ ...data, name: localization[locale], localization }));
                      }
                    }
                    return !old;
                  })}
                  aria-label='show translations'
                >
                  <TranslateIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              variant="outlined"
              size="small"
              name="hex"
              label={intl.formatMessage({ id: "EditColor.colorRGB" })}
              type="color"
              sx={textFieldStyle}
              value={data.hex || '#000000'}
              onChange={changeData}
              disabled={isStatic}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              name={"ral"}
              label={intl.formatMessage({ id: "EditColor.ral" })}
              type="text"
              sx={textFieldStyle}
              value={data.ral || ''}
              onChange={changeData}
              disabled={isStatic}
            />
          </Grid>
          {!isStatic && userInfo?.isAdmin && userInfo?.isPaying && <Grid item xs={12} sm={12} md={12}>
            <FormControlLabel control={<Checkbox checked={data.publicFlag} onChange={handlePublicChange} />} label={intl.formatMessage({ id: "Edit.published" })} />
          </Grid>}
        </Grid>
        {!isStatic && <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>}
      </Box>
    </Container>
  );
}
